import type {
    ICampaignHostEnterprisePackage,
    IGrantCampaignHostEnterprisePayload,
    IGrantCampaignHostEnterpriseResult,
} from '../../types/global/campaignHostAdmin.types';
import type {
    ICampaignHostBillingHistoryResponse,
    ICampaignHostCreationAccess,
    ICampaignHostPackage,
    ICampaignHostPricingResponse,
    ICampaignHostSubscription,
} from '../../types/global/campaignHostSubscription.types';

export const types = {
    FETCH_PACKAGES: 'campaignHostSubscription/FETCH_PACKAGES',
    FETCH_PACKAGES_SUCCESS: 'campaignHostSubscription/FETCH_PACKAGES_SUCCESS',
    FETCH_PACKAGES_FAIL: 'campaignHostSubscription/FETCH_PACKAGES_FAIL',

    FETCH_PRICING: 'campaignHostSubscription/FETCH_PRICING',
    FETCH_PRICING_SUCCESS: 'campaignHostSubscription/FETCH_PRICING_SUCCESS',
    FETCH_PRICING_FAIL: 'campaignHostSubscription/FETCH_PRICING_FAIL',

    FETCH_MINE: 'campaignHostSubscription/FETCH_MINE',
    FETCH_MINE_SUCCESS: 'campaignHostSubscription/FETCH_MINE_SUCCESS',
    FETCH_MINE_FAIL: 'campaignHostSubscription/FETCH_MINE_FAIL',

    FETCH_CREATION_ACCESS: 'campaignHostSubscription/FETCH_CREATION_ACCESS',
    FETCH_CREATION_ACCESS_SUCCESS: 'campaignHostSubscription/FETCH_CREATION_ACCESS_SUCCESS',
    FETCH_CREATION_ACCESS_FAIL: 'campaignHostSubscription/FETCH_CREATION_ACCESS_FAIL',

    FETCH_BILLING_HISTORY: 'campaignHostSubscription/FETCH_BILLING_HISTORY',
    FETCH_BILLING_HISTORY_SUCCESS: 'campaignHostSubscription/FETCH_BILLING_HISTORY_SUCCESS',
    FETCH_BILLING_HISTORY_FAIL: 'campaignHostSubscription/FETCH_BILLING_HISTORY_FAIL',

    SUBSCRIBE_MONTHLY: 'campaignHostSubscription/SUBSCRIBE_MONTHLY',
    SUBSCRIBE_MONTHLY_SUCCESS: 'campaignHostSubscription/SUBSCRIBE_MONTHLY_SUCCESS',
    SUBSCRIBE_MONTHLY_FAIL: 'campaignHostSubscription/SUBSCRIBE_MONTHLY_FAIL',

    PURCHASE_LTD: 'campaignHostSubscription/PURCHASE_LTD',
    PURCHASE_LTD_SUCCESS: 'campaignHostSubscription/PURCHASE_LTD_SUCCESS',
    PURCHASE_LTD_FAIL: 'campaignHostSubscription/PURCHASE_LTD_FAIL',

    CANCEL_SUBSCRIPTION: 'campaignHostSubscription/CANCEL_SUBSCRIPTION',
    CANCEL_SUBSCRIPTION_SUCCESS: 'campaignHostSubscription/CANCEL_SUBSCRIPTION_SUCCESS',
    CANCEL_SUBSCRIPTION_FAIL: 'campaignHostSubscription/CANCEL_SUBSCRIPTION_FAIL',

    UPDATE_PAYMENT_METHOD: 'campaignHostSubscription/UPDATE_PAYMENT_METHOD',
    UPDATE_PAYMENT_METHOD_SUCCESS: 'campaignHostSubscription/UPDATE_PAYMENT_METHOD_SUCCESS',
    UPDATE_PAYMENT_METHOD_FAIL: 'campaignHostSubscription/UPDATE_PAYMENT_METHOD_FAIL',

    SET_TIER_MODAL_OPEN: 'campaignHostSubscription/SET_TIER_MODAL_OPEN',
    CLEAR_CHECKOUT: 'campaignHostSubscription/CLEAR_CHECKOUT',

    VERIFY_PAYSTACK: 'campaignHostSubscription/VERIFY_PAYSTACK',
    VERIFY_PAYSTACK_SUCCESS: 'campaignHostSubscription/VERIFY_PAYSTACK_SUCCESS',
    VERIFY_PAYSTACK_FAIL: 'campaignHostSubscription/VERIFY_PAYSTACK_FAIL',

    FETCH_ENTERPRISE_PACKAGE: 'campaignHostSubscription/FETCH_ENTERPRISE_PACKAGE',
    FETCH_ENTERPRISE_PACKAGE_SUCCESS: 'campaignHostSubscription/FETCH_ENTERPRISE_PACKAGE_SUCCESS',
    FETCH_ENTERPRISE_PACKAGE_FAIL: 'campaignHostSubscription/FETCH_ENTERPRISE_PACKAGE_FAIL',

    GRANT_ENTERPRISE: 'campaignHostSubscription/GRANT_ENTERPRISE',
    GRANT_ENTERPRISE_SUCCESS: 'campaignHostSubscription/GRANT_ENTERPRISE_SUCCESS',
    GRANT_ENTERPRISE_FAIL: 'campaignHostSubscription/GRANT_ENTERPRISE_FAIL',
    CLEAR_ENTERPRISE_GRANT: 'campaignHostSubscription/CLEAR_ENTERPRISE_GRANT',

    SUBMIT_ENTERPRISE_SUPPORT: 'campaignHostSubscription/SUBMIT_ENTERPRISE_SUPPORT',
    SUBMIT_ENTERPRISE_SUPPORT_SUCCESS: 'campaignHostSubscription/SUBMIT_ENTERPRISE_SUPPORT_SUCCESS',
    SUBMIT_ENTERPRISE_SUPPORT_FAIL: 'campaignHostSubscription/SUBMIT_ENTERPRISE_SUPPORT_FAIL',
};

export interface ICampaignHostSubscriptionState {
    pricing: ICampaignHostPricingResponse | null;
    /** GET /campaign-host-packages (admin catalog, stripe price picker, etc.) */
    catalogPackages: ICampaignHostPackage[];
    subscription: ICampaignHostSubscription | null;
    creationAccess: ICampaignHostCreationAccess | null;
    billingHistory: ICampaignHostBillingHistoryResponse | null;

    fetchingCatalogPackages: boolean;
    fetchingPricing: boolean;
    fetchingMine: boolean;
    fetchingCreationAccess: boolean;
    fetchingBillingHistory: boolean;
    checkingOut: boolean;
    canceling: boolean;
    updatingPaymentMethod: boolean;

    tierModalOpen: boolean;
    checkoutClientSecret: string | null;
    checkoutPaymentIntentId: string | null;
    error: unknown;

    /** Super-admin enterprise */
    enterprisePackage: ICampaignHostEnterprisePackage | null;
    fetchingEnterprisePackage: boolean;
    grantingEnterprise: boolean;
    enterpriseGrantResult: IGrantCampaignHostEnterpriseResult | null;
}

export type { IGrantCampaignHostEnterprisePayload, IGrantCampaignHostEnterpriseResult };

export interface ISubscribeMonthlyPayload {
    packageId: string;
    paymentMethodId?: string;
    /** Paystack minor units (kobo) — optional; backend resolves from plan if omitted */
    amount?: number;
    callback_url?: string;
    callbackUrl?: string;
}

export interface IPurchaseLtdPayload {
    packageId?: string;
    paymentMethodId?: string;
    amount?: number;
    callback_url?: string;
    callbackUrl?: string;
}

export interface ICancelSubscriptionPayload {
    subscriptionId: string;
    cancelImmediately?: boolean;
}

export interface IUpdatePaymentMethodPayload {
    subscriptionId: string;
    paymentMethodId?: string;
}

export interface IFetchBillingHistoryPayload {
    subscriptionId?: string;
    limit?: number;
    skip?: number;
}

export interface IVerifyCampaignHostPaystackPayload {
    reference?: string;
    transaction_id?: string;
    tx_ref?: string;
    provider?: 'paystack' | 'flutterwave';
}

export type { ICampaignHostPackage, ICampaignHostPricingResponse, ICampaignHostSubscription };
